import { useQuery } from "@powersync/tanstack-react-query"
import type { Observation } from "@fieldbook/common/types"
import type { PowerSyncObservationRow } from "@/lib/powersync/schema"
import { powerSyncDb } from "@/lib/powersync/db"

function parseCustomFields(raw: string | null): Observation["custom_fields"] {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function rowToObservation(row: PowerSyncObservationRow): Observation {
  return {
    ...row,
    is_collection: Boolean(row.is_collection),
    species_indeterminate: Boolean(row.species_indeterminate),
    custom_fields: parseCustomFields(row.custom_fields),
    recorders: JSON.parse(row.recorders ?? "[]"),
  } as unknown as Observation
}

/**
 * Query the local PowerSync DB for an observation linked to a collection.
 * For use outside React (e.g. in route loaders).
 */
export async function getObservationForCollection(
  collectionId: string,
): Promise<Observation | null> {
  const row = await powerSyncDb.getOptional<PowerSyncObservationRow>(
    "SELECT * FROM observations WHERE collection_id = ? AND is_collection = 1 LIMIT 1",
    [collectionId],
  )
  return row ? rowToObservation(row) : null
}

/**
 * Hook to get the observation linked to a collection.
 *
 * Reads directly from PowerSync local SQLite — reactive to local writes
 * and synced changes from other devices.
 */
export const useObservationForCollection = ({
  collectionId,
}: {
  collectionId?: string | null
  projectId?: string
}) => {
  const query = useQuery<PowerSyncObservationRow>({
    queryKey: ["observations", "detail", "byCollection", collectionId],
    query:
      "SELECT * FROM observations WHERE collection_id = ? AND is_collection = 1 LIMIT 1",
    parameters: [collectionId ?? ""],
    enabled: Boolean(collectionId),
  })

  const row = query.data?.[0]

  // null = query ran, entity not found; undefined = query hasn't resolved yet
  const data = row ? rowToObservation(row) : query.data ? null : undefined

  return {
    data,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
  }
}
